import { FC } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dropdown } from 'primereact/dropdown';
import { MapPin } from 'lucide-react';

// TODO: confirm the correct Help Center destination with the team before shipping.
export const HELP_CENTER_URL = 'https://newclassrooms-12mm.help.userguiding.com/en/';

export type ClassesRosterEmptyStateVariant = 'no-students' | 'no-classes';

interface Props {
  variant: ClassesRosterEmptyStateVariant;
  isAdmin?: boolean;
  onCreateClass: () => void;
}

const ClassesRosterEmptyState: FC<Props> = ({ variant, isAdmin = false, onCreateClass }) => {
  const createLabel = isAdmin ? 'Create new class' : 'Create your first class';
  // Admins get the "All sites" filter, but only on the no-students state; the
  // no-classes state mirrors the teacher layout (search only).
  const showSiteFilter = isAdmin && variant === 'no-students';

  return (
    <div className="flex flex-column w-full gap-lg">
      {/* Controls stay disabled in every empty state since there's nothing to filter yet. */}
      <div className="flex flex-column md:flex-row gap-md w-full">
        {showSiteFilter && (
          <Dropdown
            disabled
            value="all"
            options={[{ label: 'All sites', value: 'all' }]}
            optionLabel="label"
            className="nc_classes-site-filter"
            valueTemplate={() => (
              <span className="flex align-items-center gap-sm">
                <MapPin size={16} aria-hidden /> All sites
              </span>
            )}
            aria-label="Filter by site"
          />
        )}
        <IconField iconPosition="left" className="nc_classes-search flex-grow-1">
          <InputIcon className="nc_icon-nc_search icon-sm ml-3"> </InputIcon>
          <InputText
            disabled
            placeholder="Search classes by name, section or grade"
            className="w-full ml-2 mr-2"
            aria-label="Search classes by name, section or grade"
          />
        </IconField>
      </div>

      <div className="flex flex-column align-items-center justify-content-center text-center gap-lg py-8 w-full">
        {variant === 'no-students' ? (
          <div className="flex flex-column align-items-center gap-sm">
            <h3>No students have been enrolled yet</h3>
            {/* Teachers are told to check with their admin first; admins lead with the Help Center. */}
            {isAdmin ? (
              <p className="m-0">
                Reach out to our{' '}
                <a href={HELP_CENTER_URL} target="_blank" rel="noreferrer">
                  Help Center
                </a>{' '}
                to get started,
                <br />
                or check with your school administrator.
                <br />
                You can still create a class now and add students later.
              </p>
            ) : (
              <p className="m-0">
                Check with your school administrator,
                <br />
                or reach out to our{' '}
                <a href={HELP_CENTER_URL} target="_blank" rel="noreferrer">
                  Help Center
                </a>{' '}
                to get started.
                <br />
                You can still create a class now and add students later.
              </p>
            )}
          </div>
        ) : (
          <h3>No class created yet</h3>
        )}

        <Button
          onClick={onCreateClass}
          label={createLabel}
          icon="nc_icon-nc_add icon-sm"
          rounded
          aria-label={createLabel}
          id="emptyStateCreateClassButton"
        />
      </div>
    </div>
  );
};

export default ClassesRosterEmptyState;
